/**
 * Vertex format. In FACE there is no block: a transaction *is* a vertex in
 * the DAG, referencing the tips it saw when it was created. Signing and
 * validating one vertex is the whole of the write path.
 */

import { canonicalBytes, fromHex, toHex, utf8, type Hex } from './bytes.ts';
import {
  verifyAssertion, type DeviceAssertion, type DeviceCredential,
} from './webauthn.ts';
import { checkPow, solvePow, tag } from './hash.ts';
import {
  addressFromPublicKey, isValidAddress, normalizeAddress, pqSign, pqVerify, sign, verify,
} from './keys.ts';
import { params, type ChainParams } from './params.ts';
import { BANDS, CODE_BYTES } from './identity.ts';
import { SKETCH_BYTES } from './fuzzy.ts';
import { mintRewardAt } from './mint.ts';

export type VertexId = Hex;

/**
 * Announces a new authenticator for this wallet.
 *
 * Takes effect only after the change window, and only if the current
 * device has not cancelled it. Signed by the account keys, which the owner
 * still derives from their face after losing the phone.
 */
export interface RotateBody {
  device: DeviceCredential;
}

/** Releases, or withdraws, a guarded transfer or a device change. */
export interface GuardBody {
  /** The guarded transfer this refers to. */
  ref: VertexId;
}

export interface TransferBody {
  to: string;
  /** Grains, decimal string so the wire format has no float anywhere. */
  amount: string;
  /** Optional sealed note: AES-GCM to the recipient's link key. Opaque here. */
  memo?: Hex;
}

/**
 * Evidence that a living person sat in front of a camera.
 *
 * Be clear about what this is worth. A validator has no video and no
 * audio; it can check that the challenge really was derived from the
 * beacon, and that the reported measurements clear their thresholds, and
 * nothing further. Every number here is asserted by the claimant's own
 * software. A modified client fabricates all of them, and the protocol
 * cannot tell. See WHITEPAPER.md section 4.5.
 *
 * So this is a barrier to casual abuse through the official wallet, not a
 * cryptographic guarantee, and it is described that way throughout.
 */
export interface LivenessProof {
  mode: 'motion';
  /** Recent DAG tip the challenge was bound to - makes replay impossible. */
  beacon: VertexId;
  /**
   * Movements the network demanded, derived from `beacon`. One character
   * per step from LRUDS: turn, turn back, up, down, smile.
   */
  challenge: string;
  /** Steps completed in the demanded order. */
  steps: number;
  /** Peak head-turn excursion, as thousandths of interocular width. */
  yawMilli: number;
  /** Peak vertical head excursion, same units. */
  pitchMilli: number;
  /**
   * Peak displacement of the nose tip off the line between the eyes.
   *
   * This is the one measurement a flat image cannot fake: photographing or
   * screening a face and tilting it is an affine transform, which leaves
   * the nose on that line. A real nose sticks out, so turning a real head
   * moves it off. It is the difference between "something moved" and
   * "something three-dimensional moved".
   */
  parallaxMilli: number;
  smiles: number;
  frames: number;
}

export interface MintBody {
  /**
   * The platform authenticator this wallet is bound to.
   *
   * Registered once, at the claim, and required for every spend
   * thereafter. This is what makes deriving the wallet key from a
   * photograph worthless: the key alone moves nothing.
   */
  device: DeviceCredential;
  /**
   * The account's ML-DSA-44 public key, registered once. Every later
   * vertex from this account must carry an ML-DSA signature that verifies
   * under it, so a quantum adversary who recovers the Ed25519 key from the
   * ledger still cannot move anything.
   */
  pq: Hex;
  /** 48-byte cancelable biometric code. Omitted in private mode. */
  code?: Hex;
  /** 32 band hashes for the duplicate index. Always present. */
  bands: Hex[];
  /**
   * Secure-sketch helper, so this person can rebuild this exact wallet
   * from their face plus their passphrase on any device. Public by
   * necessity and by design - see fuzzy.ts on why it is not a secret.
   */
  sketch: Hex;
  /** Commitment to the recovery secret, so a decode can be checked. */
  commit: Hex;
  /** Halving epoch the claimant is minting under. */
  epoch: number;
  /** Grains claimed; must equal the schedule for `epoch`. */
  amount: string;
  /**
   * Address of whoever invited this person, if anyone. Naming a referrer
   * releases one fifth of *their* locked airdrop. It grants the claimant
   * nothing, cannot be their own address, and is not required - an
   * uninvited person mints exactly the same amount.
   */
  referrer?: string;
  /**
   * The voucher's signatures over this identity.
   *
   * Device attestation proves a genuine authenticator with a passed
   * biometric consented. It does not prove the face was real, because one
   * phone can mint a thousand credentials. Nothing a validator can compute
   * distinguishes a photograph of a stranger from a face that was present,
   * so the only remaining evidence is another human staking their own
   * standing on it.
   *
   * A voucher may sponsor a limited number of newcomers per epoch, which
   * turns "unlimited sybils instantly" into a bounded, visible rate and
   * leaves the whole ring legible in the vouch graph. It does not make
   * sybils impossible - see SECURITY.md section 5.
   */
  vouch?: { ed: Hex; pq: Hex };
  liveness: LivenessProof;
}

/** What a voucher signs: this identity, sponsored by this address. */
export function vouchPreimage(identity: Hex, referrer: string): Uint8Array {
  return tag('face/vouch/v1', fromHex(identity), utf8(referrer));
}

export interface VertexHeader {
  v: 1;
  chain: string;
  type: 'mint' | 'transfer' | 'settle' | 'cancel' | 'rotate';
  /** Issuer public key, hex. */
  from: Hex;
  nonce: number;
  parents: VertexId[];
  ts: number;
  body: TransferBody | MintBody | GuardBody | RotateBody;
}

export interface Vertex extends VertexHeader {
  pow: string;
  sig: Hex;
  /**
   * A fresh assertion from the issuer's registered authenticator, over
   * this exact vertex. Consent per transaction, verified by hardware the
   * attacker does not control, and checkable by every validator.
   */
  attest?: DeviceAssertion;
  /** ML-DSA-44 signature over the same preimage as `sig`. */
  pqsig?: Hex;
}

export interface SignedVertex extends Vertex {
  id: VertexId;
}

/* --------------------------- hashing / id --------------------------- */

/** Preimage the PoW is ground over - everything except pow and sig. */
export function powPreimage(h: VertexHeader): Uint8Array {
  return tag('face/vertex/pow-preimage/v1', canonicalBytes(h));
}

/** Preimage the signature covers - everything except sig. */
export function signingPreimage(h: VertexHeader, pow: string): Uint8Array {
  return tag('face/vertex/sign/v1', canonicalBytes({ ...h, pow }));
}

export function vertexId(v: Vertex): VertexId {
  return toHex(tag('face/vertex/id/v1', canonicalBytes(stripId(v))));
}

function stripId(v: Vertex): Vertex {
  const { v: ver, chain, type, from, nonce, parents, ts, body, pow, sig, attest, pqsig } = v;
  return { v: ver, chain, type, from, nonce, parents, ts, body, pow, sig, attest, pqsig };
}

/**
 * What the authenticator is asked to consent to.
 *
 * The signing preimage, so the assertion is bound to this vertex and
 * nothing else: an assertion captured while paying a shop cannot be
 * lifted onto a transfer that empties the wallet.
 */
export function attestationChallenge(h: VertexHeader, pow: string): Uint8Array {
  return tag('face/attest/v1', signingPreimage(h, pow));
}

export function withId(v: Vertex): SignedVertex {
  return { ...stripId(v), id: vertexId(v) };
}

/* ------------------------------ building ---------------------------- */

export interface BuildOptions {
  parents: VertexId[];
  /**
   * Asks the platform authenticator to consent to this vertex. Supplied
   * by the wallet; absent in tests and in nodes that only relay.
   */
  attest?: (challenge: Uint8Array) => Promise<DeviceAssertion>;
  /** Extra difficulty on top of the base, charged to noisy senders. */
  extraPowBits?: number;
  now?: number;
  onPowProgress?: (tries: number) => void;
  chainParams?: ChainParams;
}

export async function buildVertex(
  secretKey: Uint8Array,
  publicKey: Uint8Array,
  type: VertexHeader['type'],
  nonce: number,
  body: VertexHeader['body'],
  opts: BuildOptions,
): Promise<SignedVertex> {
  const p = opts.chainParams ?? params();
  const header: VertexHeader = {
    v: 1,
    chain: p.chainId,
    type,
    from: toHex(publicKey),
    nonce,
    parents: [...opts.parents].sort(),
    ts: opts.now ?? Date.now(),
    body,
  };
  const required = Math.min(p.maxPowBits, p.basePowBits + (opts.extraPowBits ?? 0));
  const pow = await solvePow(powPreimage(header), required, opts.onPowProgress);
  const preimage = signingPreimage(header, pow);
  const sig = toHex(sign(secretKey, preimage));
  const pqsig = toHex(pqSign(secretKey, preimage));
  const attest = opts.attest ? await opts.attest(attestationChallenge(header, pow)) : undefined;
  return withId({ ...header, pow, sig, attest, pqsig });
}

/* ----------------------------- validation --------------------------- */

export interface StatelessResult {
  ok: boolean;
  reason?: string;
}

const HEX32 = /^[0-9a-f]{64}$/;
const HEX64 = /^[0-9a-f]{128}$/;

/**
 * Everything checkable about a vertex without consulting ledger state:
 * shape, signature, work, timestamp sanity, schedule arithmetic. A node
 * runs this before it will even gossip a vertex onward, so malformed
 * traffic dies at the first hop instead of fanning out.
 */
export function validateStateless(
  v: SignedVertex,
  opts: { now?: number; requiredPowBits?: number; chainParams?: ChainParams } = {},
): StatelessResult {
  const p = opts.chainParams ?? params();
  const now = opts.now ?? Date.now();
  const bad = (reason: string): StatelessResult => ({ ok: false, reason });

  if (!v || typeof v !== 'object') return bad('not an object');
  if (v.v !== 1) return bad('unsupported version');
  if (v.chain !== p.chainId) return bad(`wrong chain: ${v.chain}`);
  if (!['mint', 'transfer', 'settle', 'cancel', 'rotate'].includes(v.type)) return bad('unknown vertex type');
  if (!HEX32.test(v.from)) return bad('malformed issuer key');
  if (!HEX64.test(v.sig)) return bad('malformed signature');
  if (typeof v.pow !== 'string' || v.pow.length > 32) return bad('malformed pow nonce');
  if (!Number.isInteger(v.nonce) || v.nonce < 0) return bad('malformed nonce');
  if (!Number.isInteger(v.ts)) return bad('malformed timestamp');
  if (v.ts > now + p.maxClockSkewMs) return bad('timestamp is in the future');
  if (v.ts < p.genesisTimeMs - p.maxClockSkewMs) return bad('timestamp precedes genesis');

  if (!Array.isArray(v.parents)) return bad('parents must be a list');
  if (v.parents.length < p.minParents || v.parents.length > p.maxParents) {
    return bad(`vertex must reference ${p.minParents}-${p.maxParents} parents`);
  }
  if (new Set(v.parents).size !== v.parents.length) return bad('duplicate parent reference');
  for (const parent of v.parents) if (!HEX32.test(parent)) return bad('malformed parent id');
  const sorted = [...v.parents].sort();
  for (let i = 0; i < sorted.length; i++) if (sorted[i] !== v.parents[i]) return bad('parents must be sorted');

  if (v.type === 'rotate') {
    const b = v.body as unknown as RotateBody;
    const d = b?.device;
    if (!d || typeof d !== 'object') return bad('missing device credential');
    if (typeof d.id !== 'string' || d.id.length < 8 || d.id.length > 512) return bad('malformed credential id');
    if (typeof d.pub !== 'string' || d.pub.length !== 130 || !/^04[0-9a-f]+$/.test(d.pub)) {
      return bad('malformed credential public key');
    }
    // Asserted by the *new* device, so a rotation cannot name a credential
    // its author does not actually hold.
    if (!v.attest) return bad('a device change must be asserted by the new device');
    const a = verifyAssertion(d, v.attest, attestationChallenge(headerOf(v), v.pow));
    if (!a.ok) return bad(`new device assertion failed: ${a.reason}`);
    if (!a.userVerified) return bad('the new device did not verify the user');
  } else if (v.type === 'settle' || v.type === 'cancel') {
    const b = v.body as unknown as GuardBody;
    if (!b || typeof b !== 'object') return bad('missing guard body');
    if (!HEX32.test(b.ref)) return bad('malformed guarded transfer reference');
  } else if (v.type === 'transfer') {
    const b = v.body as TransferBody;
    if (!b || typeof b !== 'object') return bad('missing transfer body');
    if (typeof b.to !== 'string' || !isValidAddress(b.to)) return bad('invalid recipient address');
    if (normalizeAddress(b.to) !== b.to) return bad('recipient address is not canonical');
    let amount: bigint;
    try {
      if (!/^[0-9]+$/.test(b.amount)) return bad('amount must be a decimal grain string');
      amount = BigInt(b.amount);
    } catch {
      return bad('unparseable amount');
    }
    if (amount <= 0n) return bad('amount must be positive');
    if (b.memo !== undefined && (typeof b.memo !== 'string' || b.memo.length > 1024 || !/^[0-9a-f]*$/.test(b.memo))) {
      return bad('malformed memo');
    }
    if (addressFromPublicKey(fromHex(v.from)) === b.to) return bad('cannot send to yourself');
  } else {
    const b = v.body as MintBody;
    if (!b || typeof b !== 'object') return bad('missing mint body');
    if (!Array.isArray(b.bands) || b.bands.length !== BANDS) return bad(`mint must publish ${BANDS} band hashes`);
    for (const band of b.bands) if (typeof band !== 'string' || !/^[0-9a-f]{32}$/.test(band)) return bad('malformed band hash');
    if (b.code !== undefined && (typeof b.code !== 'string' || b.code.length !== CODE_BYTES * 2 || !/^[0-9a-f]+$/.test(b.code))) {
      return bad('malformed biometric code');
    }
    // Without a well-formed sketch this person could never recover their
    // wallet, and a face gets exactly one wallet - so a claim that would
    // strand them is refused rather than accepted and regretted.
    if (typeof b.sketch !== 'string' || b.sketch.length !== SKETCH_BYTES * 2 || !/^[0-9a-f]+$/.test(b.sketch)) {
      return bad('malformed recovery sketch');
    }
    if (typeof b.commit !== 'string' || !HEX32.test(b.commit)) return bad('malformed recovery commitment');
    if (!Number.isInteger(b.epoch) || b.epoch < 0) return bad('malformed halving epoch');
    if (b.referrer !== undefined) {
      if (typeof b.referrer !== 'string' || !isValidAddress(b.referrer)) return bad('invalid referrer address');
      if (normalizeAddress(b.referrer) !== b.referrer) return bad('referrer address is not canonical');
      if (addressFromPublicKey(fromHex(v.from)) === b.referrer) return bad('cannot refer yourself');
    }
    if (b.vouch !== undefined) {
      if (!b.referrer) return bad('a vouch without a referrer means nothing');
      if (typeof b.vouch !== 'object' || !HEX64.test(b.vouch.ed ?? '')) return bad('malformed vouch signature');
      if (typeof b.vouch.pq !== 'string' || b.vouch.pq.length !== 4840) return bad('malformed vouch signature');
    }
    const expected = mintRewardAt(v.ts, p);
    if (expected <= 0n) return bad('minting has ended - the halving schedule is exhausted');
    if (!/^[0-9]+$/.test(b.amount) || BigInt(b.amount) !== expected) {
      return bad(`mint amount does not match the schedule for this timestamp (expected ${expected})`);
    }
    const live = b.liveness;
    if (!live || typeof live !== 'object') return bad('missing liveness proof');
    if (live.mode !== 'motion') return bad('unknown liveness evidence mode');
    if (!HEX32.test(live.beacon)) return bad('malformed liveness beacon');
    if (typeof live.challenge !== 'string' || !CHALLENGE_RE.test(live.challenge)) return bad('malformed challenge');
    for (const n of [live.steps, live.yawMilli, live.pitchMilli, live.parallaxMilli, live.smiles, live.frames]) {
      if (!Number.isInteger(n) || n < 0) return bad('malformed liveness metrics');
    }
    if (challengeForBeacon(live.beacon, live.challenge.length) !== live.challenge) {
      return bad('the challenge was not derived from the claimed beacon');
    }
    // The beacon must be one of this vertex's parents, which are real
    // vertices the issuer had to have received. Without this the "beacon"
    // is 32 bytes of the claimant's choosing: grind values until the
    // challenge matches a recording you already hold, and the
    // unpredictability the whole scheme rests on evaporates. Requiring it
    // to be a parent is checkable without any ledger state, because the
    // parents travel in the vertex.
    if (!v.parents.includes(live.beacon)) {
      return bad('the liveness beacon is not among this vertex\'s parents');
    }
    if (live.steps !== live.challenge.length) return bad('not every challenge step was completed');
    if (live.frames < MIN_LIVENESS_FRAMES) return bad('too few liveness frames');
    if (/[LR]/.test(live.challenge) && live.yawMilli < MIN_YAW_MILLI) return bad('head turn below threshold');
    if (/[UD]/.test(live.challenge) && live.pitchMilli < MIN_PITCH_MILLI) return bad('head tilt below threshold');
    if (live.smiles < (live.challenge.match(/S/g)?.length ?? 0)) return bad('too few smiles');
    if (live.parallaxMilli < MIN_PARALLAX_MILLI) return bad('no three-dimensional movement detected');


    // A claim must register a platform authenticator and be asserted by
    // it. This is the only part of a claim a validator can actually check
    // about the human: not that they were alive, but that a secure
    // element that performs its own biometric check consented, and that a
    // script with no such hardware cannot forge.
    if (typeof b.pq !== 'string' || b.pq.length !== 2624 || !/^[0-9a-f]+$/.test(b.pq)) {
      return bad('malformed post-quantum public key');
    }
    if (!v.pqsig || !pqVerify(b.pq, signingPreimage(headerOf(v), v.pow), v.pqsig)) {
      return bad('post-quantum signature does not verify');
    }
    const dev = b.device;
    if (!dev || typeof dev !== 'object') return bad('missing device credential');
    if (typeof dev.id !== 'string' || dev.id.length < 8 || dev.id.length > 512) return bad('malformed credential id');
    if (typeof dev.pub !== 'string' || dev.pub.length !== 130 || !/^04[0-9a-f]+$/.test(dev.pub)) {
      return bad('malformed credential public key');
    }
    if (!v.attest) return bad('a claim must be asserted by its authenticator');
    const assertion = verifyAssertion(dev, v.attest, attestationChallenge(headerOf(v), v.pow));
    if (!assertion.ok) return bad(`device assertion failed: ${assertion.reason}`);
    if (!assertion.userVerified) return bad('the authenticator did not verify the user');
    if (!assertion.userPresent) return bad('the authenticator did not confirm user presence');
  }

  const header = headerOf(v);
  const required = Math.min(p.maxPowBits, opts.requiredPowBits ?? p.basePowBits);
  if (!checkPow(powPreimage(header), v.pow, required)) return bad(`insufficient proof of work (need ${required} bits)`);
  if (!verify(v.from, signingPreimage(header, v.pow), v.sig)) return bad('signature does not verify');
  if (vertexId(v) !== v.id) return bad('vertex id does not match its contents');

  return { ok: true };
}

/* --------------------------- the challenge -------------------------- */

/** Movements the wallet may demand. Turn, turn back, up, down, smile. */
export const CHALLENGE_ACTIONS = 'LRUDS';
export const CHALLENGE_STEPS = 4;
const CHALLENGE_RE = /^[LRUDS]{3,8}$/;

/** Thresholds a claim's measurements must clear. */
export const MIN_LIVENESS_FRAMES = 40;
export const MIN_YAW_MILLI = 180;
export const MIN_PITCH_MILLI = 110;
export const MIN_PARALLAX_MILLI = 35;

/**
 * The liveness challenge is a deterministic function of a recent DAG tip,
 * so a claimant cannot choose their own and cannot reuse a recording made
 * before that tip existed. With five actions over four steps there are 625
 * sequences, which is the number of clips someone would have to have
 * prepared in advance of a vertex they could not predict.
 */
export function challengeForBeacon(beacon: VertexId, count = CHALLENGE_STEPS): string {
  const d = tag('face/liveness/challenge/v3', fromHex(beacon));
  let out = '';
  for (let i = 0; i < count; i++) out += CHALLENGE_ACTIONS[d[i] % CHALLENGE_ACTIONS.length];
  return out;
}

/** The signed part of a vertex, without the signature or the assertion. */
export function headerOf(v: Vertex): VertexHeader {
  return {
    v: v.v, chain: v.chain, type: v.type, from: v.from,
    nonce: v.nonce, parents: v.parents, ts: v.ts, body: v.body,
  };
}

export function senderAddress(v: VertexHeader): string {
  return addressFromPublicKey(fromHex(v.from));
}

/** Conflict-set key. Two vertices sharing one cannot both be accepted. */
export function conflictKey(v: SignedVertex): string {
  return `${senderAddress(v)}#${v.nonce}`;
}
